import React, { useState, useMemo, useEffect } from "react";
import { Drawer, Tabs, Input, Button, message, Spin } from "antd";
import { ArrowDownLeft, ArrowUpRight, FileText } from "lucide-react";
import {
  useCashStatusQuery,
  usecreateCashInMutation,
  usecreateCashOutMutation,
} from "../../../Services";
import { DenomRow } from "./DenomRow";
import { drawerTitle } from "../../../components";

interface CashFlowModalProps {
  open: boolean;
  onClose: () => void;
}

export const CashFlowModal: React.FC<CashFlowModalProps> = ({
  open,
  onClose,
}) => {
  const BILLS = [5000, 2000, 1000, 500, 100, 50, 20];
  const COINS = [10, 5, 2, 1];
  const DENOMINATIONS = [...BILLS, ...COINS];

  const [activeTab, setActiveTab] = useState<string>("cash-in");
  const [cashInAmount, setCashInAmount] = useState<string>("");
  const [denominations, setDenominations] = useState<Record<number, number>>(
    () => DENOMINATIONS.reduce((acc, d) => ({ ...acc, [d]: 0 }), {}),
  );
  const [note, setNote] = useState<string>("");
  const [activeShift, setActiveShift] = useState<any>(null);

  const calculatedOutTotal = useMemo(() => {
    return Object.entries(denominations).reduce((acc, [denom, count]) => {
      return acc + Number(denom) * (count || 0);
    }, 0);
  }, [denominations]);

  const handleReset = () => {
    setCashInAmount("");
    setDenominations(
      DENOMINATIONS.reduce((acc, d) => ({ ...acc, [d]: 0 }), {}),
    );
    setNote("");
  };

  const handleCountChange = (denom: number, val: number) => {
    if (val < 0) return;
    setDenominations((prev) => ({
      ...prev,
      [denom]: val,
    }));
  };

  const {
    data: cashStatusData,
    isLoading: isLoadingStatus,
    refetch: fetchStatus,
  } = useCashStatusQuery(undefined, { enabled: open });

  useEffect(() => {
    if (cashStatusData?.success) {
      setActiveShift(cashStatusData.active ? cashStatusData.cash_in : null);
      if (cashStatusData.active) {
        setActiveTab("cash-out");
      } else {
        setActiveTab("cash-in");
      }
    }
  }, [cashStatusData]);

  const cashInMutation = usecreateCashInMutation();
  const cashOutMutation = usecreateCashOutMutation();

  const handleSubmit = async () => {
    if (activeTab === "cash-in") {
      const amt = parseFloat(cashInAmount);
      if (isNaN(amt) || amt <= 0) {
        message.error("Please enter a valid Cash In amount greater than 0.");
        return;
      }
      cashInMutation.mutate(
        { amount: amt },
        {
          onSuccess: (res) => {
            if (res.success) {
              message.success(res.message || "Cash In recorded successfully.");
              handleReset();
              fetchStatus();
              onClose();
            } else {
              message.error(res.message || "Failed to record Cash In.");
            }
          },
          onError: (err: any) => {
            const errMsg =
              err.response?.data?.message ||
              err.message ||
              "An unexpected error occurred.";
            message.error(errMsg);
          },
        },
      );
    } else {
      if (calculatedOutTotal <= 0) {
        message.error(
          "Please specify a valid coin/note count breakdown for Cash Out.",
        );
        return;
      }

      const denomMap: Record<string, number> = {};
      Object.entries(denominations).forEach(([d, count]) => {
        if (count > 0) {
          denomMap[d] = count;
        }
      });

      cashOutMutation.mutate(
        {
          denominations: denomMap,
          note: note,
        },
        {
          onSuccess: (res) => {
            if (res.success) {
              message.success(res.message || "Cash Out recorded successfully.");
              handleReset();
              fetchStatus();
              onClose();
            } else {
              message.error(res.message || "Failed to record Cash Out.");
            }
          },
          onError: (err: any) => {
            const errMsg =
              err.response?.data?.message ||
              err.message ||
              "An unexpected error occurred.";
            message.error(errMsg);
          },
        },
      );
    }
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        handleReset();
        onClose();
      }}
      width={600}
      placement="right"
      title={
        <div className="flex items-center justify-between w-full pr-4">
          {drawerTitle(
            "Cash Register Shift Manager",
            "Manage store float and cashier drops",
          )}
          {!isLoadingStatus && (
            <div>
              {activeShift ? (
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-md">
                  Shift Active
                </span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-200 px-2 py-0.5 rounded-md">
                  No Active Shift
                </span>
              )}
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            size="middle"
            className="h-9 px-4 text-xs border-gray-200 text-gray-500 rounded-md"
            onClick={() => {
              handleReset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            size="middle"
            type="primary"
            loading={cashInMutation.isPending || cashOutMutation.isPending}
            disabled={isLoadingStatus}
            onClick={handleSubmit}
            className={`h-9 px-5 text-xs font-semibold border-none rounded-md ${
              activeTab === "cash-in"
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-rose-600 hover:bg-rose-700"
            }`}
          >
            {activeTab === "cash-in" ? "Confirm Cash In" : "Confirm Cash Out"}
          </Button>
        </div>
      }
    >
      {isLoadingStatus ? (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <Spin size="default" />
          <span className="text-xs text-gray-400 font-medium">
            Checking register status...
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-4 h-full">
          {activeShift ? (
            <div className="p-3.5 bg-emerald-50/50 border border-emerald-100 rounded-xl flex items-center justify-between">
              <div>
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                  Active Shift Start Time
                </p>
                <p className="text-xs text-gray-700 font-medium mt-0.5">
                  {new Date(activeShift.created_at).toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                  Opening Float
                </p>
                <p className="text-base font-bold text-emerald-700 mt-0.5">
                  LKR{" "}
                  {Number(activeShift.amount).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
          ) : (
            <div className="p-3.5 bg-amber-50/50 border border-amber-100 rounded-xl">
              <p className="text-xs text-amber-800 font-medium">
                No active shift found. Please enter starting cash float below to
                start shift.
              </p>
            </div>
          )}

          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              handleReset();
            }}
            size="small"
            items={[
              {
                key: "cash-in",
                disabled: !!activeShift,
                label: (
                  <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide">
                    <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-500" />{" "}
                    Cash In
                  </span>
                ),
                children: (
                  <div className="pt-2 flex flex-col gap-3">
                    <p className="text-[11px] text-gray-400">
                      Enter the opening float amount for this shift.
                    </p>
                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden w-64">
                      <span className="px-3 h-10 flex items-center text-[11px] font-semibold text-gray-400 bg-gray-50 border-r border-gray-200">
                        LKR
                      </span>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={cashInAmount}
                        onChange={(e) => setCashInAmount(e.target.value)}
                        className="flex-1 h-10 px-3 text-xl font-semibold text-center text-gray-900 border-none outline-none bg-transparent"
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: "cash-out",
                disabled: !activeShift,
                label: (
                  <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide">
                    <ArrowUpRight className="w-3.5 h-3.5 text-rose-500" /> Cash
                    Out
                  </span>
                ),
                children: (
                  <div className="pt-2 flex flex-col gap-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] uppercase tracking-wider text-gray-400">
                        Denomination Breakdown
                      </span>
                      <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 border border-rose-100 px-2.5 py-0.5 rounded-full">
                        LKR{" "}
                        {calculatedOutTotal.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="border border-gray-100 rounded-lg overflow-hidden">
                        <div className="bg-emerald-50 px-2 py-1.5 border-b border-gray-100">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-700">
                            Notes / Bills
                          </span>
                        </div>
                        {BILLS.map((d) => (
                          <DenomRow
                            key={d}
                            denom={d}
                            count={denominations[d] || 0}
                            onCountChange={handleCountChange}
                          />
                        ))}
                      </div>
                      <div className="border border-gray-100 rounded-lg overflow-hidden">
                        <div className="bg-amber-50 px-2 py-1.5 border-b border-gray-100">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-amber-700">
                            Coins
                          </span>
                        </div>
                        {COINS.map((d) => (
                          <DenomRow
                            key={d}
                            denom={d}
                            count={denominations[d] || 0}
                            onCountChange={handleCountChange}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Optional Remarks
                      </p>
                      <Input.TextArea
                        rows={2}
                        placeholder="Specify shift details or notes..."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        className="rounded-lg border-gray-200 text-xs"
                      />
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
    </Drawer>
  );
};
